import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Row,
  Col,
  PreviewCard,
  PreviewAltCard,
  Button,
  RSelect,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const GamificationPage = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("leaderboard");
  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [period, setPeriod] = useState("all");
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteScores, setSiteScores] = useState([]);
  const [siteScoresLoading, setSiteScoresLoading] = useState(false);

  // Déterminer le tab actif en fonction de la route
  useEffect(() => {
    const pathname = location.pathname;
    
    if (pathname.includes("/gamification/leaderboard")) {
      setActiveTab("leaderboard");
    } else if (pathname.includes("/gamification/sites")) {
      setActiveTab("sites");
    } else if (pathname.includes("/gamification/badges")) {
      setActiveTab("badges");
    } else if (pathname.includes("/gamification/points")) {
      setActiveTab("points");
    }
  }, [location.pathname]);

  useEffect(() => {
    loadSites();
    if (activeTab === "leaderboard") {
      loadLeaderboard();
    } else if (activeTab === "sites") {
      loadSiteScores();
    } else if (activeTab === "badges") {
      loadBadges();
      loadMyBadges();
    } else if (activeTab === "points") {
      loadMyStats();
      loadPointsHistory();
    }
  }, [activeTab, period, selectedSite]);

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      setSites(response.data || response || []);
    } catch (error) {
      console.error("Erreur lors du chargement des sites:", error);
    }
  };

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (selectedSite) {
        params.site_id = selectedSite;
      }
      const response = await api.getLeaderboard(params);
      setLeaderboard(response.leaderboard || []);
    } catch (error) {
      console.error("Erreur lors du chargement du classement:", error);
      toast.error("Erreur lors du chargement du classement");
    } finally {
      setLoading(false);
    }
  };

  const loadSiteScores = async () => {
    setSiteScoresLoading(true);
    try {
      const response = await api.getSiteSecurityScores({ period });
      setSiteScores(response.sites || []);
    } catch (error) {
      console.error("Erreur lors du chargement des scores par site:", error);
      toast.error("Erreur lors du chargement des scores par site");
      setSiteScores([]);
    } finally {
      setSiteScoresLoading(false);
    }
  };

  const loadMyStats = async () => {
    try {
      const response = await api.getMyStats();
      setMyStats(response);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
      toast.error("Erreur lors du chargement des statistiques");
    }
  };

  const loadBadges = async () => {
    try {
      const response = await api.getBadges();
      setBadges(response || []);
    } catch (error) {
      console.error("Erreur lors du chargement des badges:", error);
      toast.error("Erreur lors du chargement des badges");
    }
  };

  const loadMyBadges = async () => {
    try {
      const response = await api.getMyBadges();
      setMyBadges(response || []);
    } catch (error) {
      console.error("Erreur lors du chargement de mes badges:", error);
    }
  };

  const loadPointsHistory = async () => {
    try {
      const response = await api.getMyPointsHistory({ limit: 50 });
      setPointsHistory(response || []);
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique:", error);
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return "trophy";
    if (rank === 2) return "medal";
    if (rank === 3) return "medal-alt";
    return "star";
  };

  const getRankColor = (rank) => {
    if (rank === 1) return "warning";
    if (rank === 2) return "secondary";
    if (rank === 3) return "info";
    return "light";
  };

  const renderLeaderboard = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle page>Classement employés</BlockTitle>
            <BlockDes className="text-soft">
              <p>Score sécurité HSE par personne (points cumulés sur la période)</p>
            </BlockDes>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      <PreviewCard className="card-full">
        <div className="card-inner">
          <Row className="g-3 mb-4">
            <Col md="4">
              <div className="form-group">
                <label className="form-label">Période</label>
                <RSelect
                  options={[
                    { value: "all", label: "Tout le temps" },
                    { value: "month", label: "Ce mois" },
                    { value: "year", label: "Cette année" },
                  ]}
                  value={{ value: period, label: period === "all" ? "Tout le temps" : period === "month" ? "Ce mois" : "Cette année" }}
                  onChange={(option) => setPeriod(option.value)}
                />
              </div>
            </Col>
            <Col md="4">
              <div className="form-group">
                <label className="form-label">Site</label>
                <RSelect
                  options={[
                    { value: null, label: "Tous les sites" },
                    ...sites.map((site) => ({ value: site.id, label: site.name })),
                  ]}
                  value={{
                    value: selectedSite,
                    label: selectedSite ? sites.find((s) => s.id === selectedSite)?.name || "Site" : "Tous les sites",
                  }}
                  onChange={(option) => setSelectedSite(option.value)}
                />
              </div>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center p-4 text-soft">Aucun classement disponible</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: "80px" }}>Rang</th>
                    <th>Utilisateur</th>
                    <th className="text-end">Score sécurité</th>
                    <th className="text-center">Badges</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((item) => (
                    <tr key={item.user.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Icon
                            name={getRankIcon(item.rank)}
                            className={`text-${getRankColor(item.rank)} me-2`}
                            size="lg"
                          />
                          <span className="fw-bold">#{item.rank}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {item.user.avatar ? (
                            <img
                              src={item.user.avatar}
                              alt={item.user.name}
                              className="avatar avatar-md rounded-circle me-2"
                              style={{ width: '38px', height: '38px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div 
                              className="avatar bg-primary rounded-circle me-2 d-flex align-items-center justify-content-center"
                              style={{ width: '38px', height: '38px', fontSize: '20px' }}
                            >
                              <span className="text-white">
                                {item.user.name?.charAt(0)?.toUpperCase() || "U"}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="fw-bold">{item.user.name}</div>
                            <small className="text-soft">{item.user.email}</small>
                          </div>
                        </div>
                      </td>
                      <td className="text-end">
                        <span 
                          className="badge badge-sm"
                          style={{
                            backgroundColor: item.total_points >= 1000 ? '#795548' : 
                                            item.total_points >= 500 ? '#FF5722' : 
                                            item.total_points >= 100 ? '#FF9800' : '#FFD700',
                            color: '#fff'
                          }}
                        >
                          {item.total_points}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center">
                          {item.badges && item.badges.length > 0 ? (
                            item.badges.slice(0, 3).map((badge) => (
                              <span
                                key={badge.id}
                                className="badge"
                                style={{
                                  backgroundColor: badge.color || "#6c757d",
                                  color: "#fff",
                                }}
                                title={badge.name}
                              >
                                <Icon name={badge.icon || "award"} />
                              </span>
                            ))
                          ) : (
                            <span className="text-soft">-</span>
                          )}
                          {item.badges_count > 3 && (
                            <span className="badge badge-light">+{item.badges_count - 3}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </PreviewCard>
    </div>
  );

  const renderBadges = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockHeadContent>
          <BlockTitle page>Badges</BlockTitle>
          <BlockDes className="text-soft">
            <p>Découvrez tous les badges disponibles et vos accomplissements</p>
          </BlockDes>
        </BlockHeadContent>
      </BlockHead>

      {myStats && (
        <Row className="g-gs mb-4">
          <Col md="4">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-primary">{myStats.badges_count}</div>
                  <div className="text-soft">Badges obtenus</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="4">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-success">{badges.length}</div>
                  <div className="text-soft">Badges disponibles</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="4">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-warning">
                    {badges.length > 0 ? Math.round((myStats.badges_count / badges.length) * 100) : 0}%
                  </div>
                  <div className="text-soft">Progression</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
        </Row>
      )}

      <PreviewCard className="card-full">
        <div className="card-inner">
          <h6 className="title mb-4">Tous les badges</h6>
          <Row className="g-gs">
            {badges.map((badge) => (
              <Col md="6" lg="4" key={badge.id}>
                <PreviewAltCard className={`card-full ${badge.earned ? "border-success" : ""}`}>
                  <div className="card-inner">
                    <div className="text-center">
                      <div
                        className="icon-circle icon-circle-xl mx-auto mb-3"
                        style={{
                          backgroundColor: badge.color ? `${badge.color}20` : "#f0f0f0",
                          color: badge.color || "#6c757d",
                        }}
                      >
                        <Icon name={badge.icon || "award"} size="2x" />
                      </div>
                      {badge.earned && (
                        <span className="badge badge-success mb-2">
                          <Icon name="check" /> Obtenu
                        </span>
                      )}
                      <h6 className="title">{badge.name}</h6>
                      <p className="text-soft small">{badge.description}</p>
                      {badge.criteria && (
                        <div className="mt-2">
                          <small className="text-soft">
                            Critère: {getCriteriaDescription(badge.criteria)}
                          </small>
                        </div>
                      )}
                      {badge.earned_at && (
                        <div className="mt-2">
                          <small className="text-soft">
                            Obtenu le: {new Date(badge.earned_at).toLocaleDateString("fr-FR")}
                          </small>
                        </div>
                      )}
                      {badge.users_count !== undefined && (
                        <div className="mt-2">
                          <small className="text-soft">
                            <Icon name="users" /> {badge.users_count} utilisateur{badge.users_count > 1 ? "s" : ""}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </PreviewAltCard>
              </Col>
            ))}
          </Row>
        </div>
      </PreviewCard>
    </div>
  );

  const getCriteriaDescription = (criteria) => {
    if (!criteria || !criteria.type) return "";
    switch (criteria.type) {
      case "observations_count":
        return `${criteria.value} observation${criteria.value > 1 ? "s" : ""} validée${criteria.value > 1 ? "s" : ""}`;
      case "observations_positive_count":
        return `${criteria.value} observation${criteria.value > 1 ? "s" : ""} positive${criteria.value > 1 ? "s" : ""}`;
      case "total_points":
        return `${criteria.value} de score sécurité`;
      case "consecutive_days":
        return `${criteria.value} jour${criteria.value > 1 ? "s" : ""} consécutif${criteria.value > 1 ? "s" : ""}`;
      case "zero_accident":
        return `${criteria.value} jour${criteria.value > 1 ? "s" : ""} sans accident`;
      case "top_performer":
        return `Top ${criteria.position} ${criteria.period === "month" ? "mensuel" : "annuel"}`;
      default:
        return "";
    }
  };

  const renderSiteScores = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle page>Scores par site</BlockTitle>
            <BlockDes className="text-soft">
              <p>
                Agrégation par site : score sécurité (points HSE liés aux observations du site), qualité moyenne des
                observations et volume sur la période.
              </p>
            </BlockDes>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      <PreviewCard className="card-full">
        <div className="card-inner">
          <Row className="g-3 mb-4">
            <Col md="4">
              <div className="form-group">
                <label className="form-label">Période</label>
                <RSelect
                  options={[
                    { value: "all", label: "Tout le temps" },
                    { value: "month", label: "Ce mois" },
                    { value: "year", label: "Cette année" },
                  ]}
                  value={{ value: period, label: period === "all" ? "Tout le temps" : period === "month" ? "Ce mois" : "Cette année" }}
                  onChange={(option) => setPeriod(option.value)}
                />
              </div>
            </Col>
          </Row>

          {siteScoresLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : siteScores.length === 0 ? (
            <div className="text-center p-4 text-soft">Aucun site ou aucune donnée sur la période</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th className="text-end">Score sécurité</th>
                    <th className="text-end">Qualité moy. (obs.)</th>
                    <th className="text-end">Observations</th>
                  </tr>
                </thead>
                <tbody>
                  {siteScores.map((row) => (
                    <tr key={row.site_id}>
                      <td className="fw-medium">{row.site_name}</td>
                      <td className="text-end">
                        <span className="badge badge-dim bg-primary">{row.security_score}</span>
                      </td>
                      <td className="text-end text-soft">
                        {row.avg_quality_score != null ? `${row.avg_quality_score} / 100` : "—"}
                      </td>
                      <td className="text-end">{row.observations_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </PreviewCard>
    </div>
  );

  const renderPoints = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockHeadContent>
          <BlockTitle page>Mon score & historique</BlockTitle>
          <BlockDes className="text-soft">
            <p>Votre score sécurité HSE, classements et détail des gains de points</p>
          </BlockDes>
        </BlockHeadContent>
      </BlockHead>

      {myStats && (
        <Row className="g-gs mb-4">
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-primary">{myStats.total_points}</div>
                  <div className="text-soft">Score sécurité total</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-success">{myStats.month_points}</div>
                  <div className="text-soft">Ce mois</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-info">#{myStats.rank}</div>
                  <div className="text-soft">Classement global</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-warning">#{myStats.month_rank}</div>
                  <div className="text-soft">Classement mensuel</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
        </Row>
      )}

      <Row className="g-gs">
        <Col lg="8">
          <PreviewCard className="card-full">
            <div className="card-inner">
              <h6 className="title mb-4">Historique du score sécurité</h6>
              {pointsHistory.length === 0 ? (
                <div className="text-center p-4 text-soft">Aucun historique disponible</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Raison</th>
                        <th>Observation</th>
                        <th className="text-end">Gain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsHistory.map((point) => (
                        <tr key={point.id}>
                          <td>{new Date(point.created_at).toLocaleDateString("fr-FR")}</td>
                          <td>{point.reason}</td>
                          <td>
                            {point.observation ? (
                              <span className="text-soft">{point.observation.title}</span>
                            ) : (
                              <span className="text-soft">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <span className="badge badge-primary">+{point.points}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </PreviewCard>
        </Col>
        <Col lg="4">
          <PreviewCard className="card-full">
            <div className="card-inner">
              <h6 className="title mb-4">Mes badges récents</h6>
              {myBadges.length === 0 ? (
                <div className="text-center p-4 text-soft">Aucun badge obtenu</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {myBadges.slice(0, 5).map((badge) => (
                    <div key={badge.id} className="d-flex align-items-center">
                      <div
                        className="icon-circle icon-circle-md me-3"
                        style={{
                          backgroundColor: badge.color ? `${badge.color}20` : "#f0f0f0",
                          color: badge.color || "#6c757d",
                        }}
                      >
                        <Icon name={badge.icon || "award"} />
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{badge.name}</div>
                        <small className="text-soft">
                          {badge.earned_at
                            ? `Obtenu le ${new Date(badge.earned_at).toLocaleDateString("fr-FR")}`
                            : badge.description}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PreviewCard>
        </Col>
      </Row>
    </div>
  );

  return (
    <React.Fragment>
      <Head title="Score sécurité HSE" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Score sécurité HSE</BlockTitle>
              <BlockDes>
                <p>Classements employés et par site, badges et historique des gains HSE</p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Nav tabs className="nav-tabs-s2">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "leaderboard" })}
                onClick={() => {
                  setActiveTab("leaderboard");
                  history.push(`${process.env.PUBLIC_URL}/gamification/leaderboard`);
                }}
              >
                <Icon name="trophy"></Icon>
                <span>Employés</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "sites" })}
                onClick={() => {
                  setActiveTab("sites");
                  history.push(`${process.env.PUBLIC_URL}/gamification/sites`);
                }}
              >
                <Icon name="map-pin"></Icon>
                <span>Par site</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "badges" })}
                onClick={() => {
                  setActiveTab("badges");
                  history.push(`${process.env.PUBLIC_URL}/gamification/badges`);
                }}
              >
                <Icon name="award"></Icon>
                <span>Badges</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "points" })}
                onClick={() => {
                  setActiveTab("points");
                  history.push(`${process.env.PUBLIC_URL}/gamification/points`);
                }}
              >
                <Icon name="coin"></Icon>
                <span>Mon score</span>
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-4">
            <TabPane tabId="leaderboard">{renderLeaderboard()}</TabPane>
            <TabPane tabId="sites">{renderSiteScores()}</TabPane>
            <TabPane tabId="badges">{renderBadges()}</TabPane>
            <TabPane tabId="points">{renderPoints()}</TabPane>
          </TabContent>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default GamificationPage;
